import { FCM } from '@ionic-native/fcm';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AlertController, Platform } from 'ionic-angular';
import { Observable } from 'rxjs/Observable';
import { ENV } from '../../environments/environment';
import { Subject } from 'rxjs/Subject';
import { Storage } from '@ionic/storage';
import * as jwt_decode from 'jwt-decode';
import { Apollo } from 'apollo-angular';
import { HttpLink } from 'apollo-angular-link-http';
import { InMemoryCache } from 'apollo-cache-inmemory';
import { DefaultOptions } from 'apollo-client/ApolloClient';
import { User } from './models';
import { StorageProvider } from '../utils/storage';

@Injectable()
export class AuthProvider {
  private clientObj: any;
  private currentUser: any; // User

  public userSubject = new Subject<any>();
  public userData: {
    data: User;
    isSchedular: boolean;
    isInstaller: boolean;
    jwtData: {
      jwtResponse: {
        access_token: string;
        expires_in: number;
        refresh_token: string;
        scope: string;
        token_type: string;
        updated: number;
      };
      jwtDecode: {
        snn: string;
      };
    };
  } = {
    data: null,
    isSchedular: false,
    isInstaller: false,
    jwtData: {
      jwtResponse: {
        access_token: '',
        expires_in: null,
        refresh_token: '',
        scope: '',
        token_type: '',
        updated: 0
      },
      jwtDecode: {
        snn: ''
      }
    }
  };

  constructor(
    private http: HttpClient,
    private storage: Storage,
    private alertCtrl: AlertController,
    private platform: Platform,
    private apollo: Apollo,
    private httpLink: HttpLink,
    private fcm: FCM,
    private storageProvider: StorageProvider
  ) {
    if (this.platform.is('ios')) {
      this.clientObj = ENV.ios.config;
    } else {
      this.clientObj = ENV.android.config;
    }

    this.userSubject.next('any');
  }

  forgotPassword(email: string): Observable<any> {
    return Observable.create(observer => {
      let obj = {
        username: email
      };
      this.http
        .post(ENV.jwtCredentialAuthority + '/accounts/forgot-password', obj)
        .subscribe(result => {
          console.log("result", result)
          observer.next(result);
          observer.complete();
        },
        err => {
          console.log("err", err)
          observer.next(err);
          observer.complete();
        });
    });
  }
  verificationResend(email: string): Observable<any> {
    return Observable.create(observer => {
      let obj = {
        username: email
      };
      this.http
        .post(ENV.jwtCredentialAuthority + '/accounts/resend-validate-email', obj)
        .subscribe(result => {
          observer.next(result);
          observer.complete();
        },
        error => {
          observer.error(error);
        }
        );
    });
  }

  loginUser(email, password, twoFaCode?) {
    let observer;
    let obs = Observable.create(obs => {
      observer = obs;
      let obj = {
        grant_type: 'password',
        client_id: this.clientObj.id,
        client_secret: this.clientObj.token,
        username: email,
        password: password,
        twoFaCode: twoFaCode,
        scope: 'sensordesk'
      };
      //if (authcode) obj.twoFaCode = twoFaCode;
      this.http
        .post(ENV.jwtCredentialAuthority + '/oauth/token', obj)
        .subscribe(
          result => {
            let decode = jwt_decode((result as any).access_token);
            this.userData.jwtData = {
              jwtResponse: result as any,
              jwtDecode: decode
            };
            this.userData.jwtData.jwtResponse.updated = new Date().getTime();
            // this.userData.jwtUser = this.jwtUser;

            this.currentUser = {
              uid: this.userData.jwtData.jwtDecode.snn
            };
            localStorage.setItem('currentUser', this.userData.jwtData.jwtDecode.snn)

            this.getSignInUser().subscribe(
              signinResult => {
                observer.next(this.currentUser);
                observer.complete();
                // store user data
                this.storage.set('user', this.userData).then((value: any) => {
                  observer.next(this.userData.jwtData);
                  observer.complete();
                  //this.createRefresh();
                });
              },
              error => {
                observer.error(error);
              }
            );
          },
          error => {
            console.log('got error here');
            observer.error(error);
          }
        );
    });
    return obs;
  }

  checkUser() {
    // this returns current user status, to check on connected status (or auto login)
    return Observable.create(observer => {
      this.storage
        .get('user')
        .then(
          (value: any) => {
            if (value && JSON.stringify(value) !== '{}') {
              this.userData = value;
              if (this.userData.jwtData) {
                this.loginWithToken().subscribe(
                  result => {
                    observer.next(this.userData);
                  },
                  e => {
                    observer.error(e);
                  },
                  () => {
                    console.log('completed check user');
                  }
                );
              } else {
                observer.complete();
              }
            } else {
              observer.complete();
            }
          },
          error => {
            observer.complete();
          }
        )
        .catch(e => {
          observer.complete();
        });
    });
  }

  logoutUser() {
    let observer;
    let obs = Observable.create(obs => {
      observer = obs;
    });

    this.storage.set('user', {}).then((value: any) => {
      this.userData = {
        data: null,
        jwtData: null,
        isSchedular: false,
        isInstaller: false,
      };
      localStorage.removeItem('currentUser')
      try {
        observer.next(this.userData);
        observer.complete();
      } catch (e) {
        // error
      }
    });

    return obs;
  }

  loginWithToken() {
    let jwtobj = this.userData.jwtData;
    return Observable.create(observer => {
      let refresh_token = jwtobj.jwtResponse.refresh_token;
      let obj = {
        grant_type: 'refresh_token',
        client_id: this.clientObj.id,
        client_secret: this.clientObj.token,
        refresh_token: refresh_token,
        scope: 'sensordesk'
      };
      this.http
        .post(ENV.jwtCredentialAuthority + '/oauth/token', obj)
        .subscribe(
          result => {
            const resultObj = result as any;
            let decode = jwt_decode(resultObj.access_token);
            let jwtData = this.userData.jwtData;
            jwtData.jwtResponse.access_token = resultObj.access_token;
            jwtData.jwtResponse.expires_in = resultObj.expires_in;
            jwtData.jwtResponse.updated = new Date().getTime();
            jwtData.jwtDecode = decode;

            this.currentUser = {
              uid: this.userData.jwtData.jwtDecode.snn
            };
            localStorage.setItem('currentUser', this.userData.jwtData.jwtDecode.snn)
            this.getSignInUser().subscribe(
              result => {
                this.storage.set('user', this.userData).then((value: any) => {
                  observer.next(this.userData);
                });
              },
              error => {
                observer.error(error);
              }
            );
          },
          error => {
            // error
            console.log('ERROR on token refresh');
            observer.error(error);
            this.userSubject.error('cant refresh token');
          }
        );
    });
  }

  getUserId() {
    return this.userData?.data?.uid;
  }

  getSignInUser() {
    return Observable.create(observer => {
      let key = this.currentUser.uid;
      if (key == '' || key == '0') {
        observer.complete('no key');
      } else {
        this.getUser(key).subscribe(
          userResult => {
            this.userData.data = userResult;
            this.userData.isSchedular = !![...userResult.roles].find(element => element.includes('task-scheduler'));
            this.userData.isInstaller = !![...userResult.roles].find(element => element.includes('installer'));
            this.userSubject.next(userResult);
            //trg.authObserver.next(trg.userData);
            this.initGraphQL().subscribe();
            observer.next(this.userData);
            observer.complete();
          },
          error => {
            observer.error(error);
          }
        );
      }
    });
  }

  getUser(idkey?: string) {
    let id = !idkey ? this.currentUser.uid : idkey;
    return Observable.create(observer => {
      const hasQueryBeenCalledBefore = this.storageProvider.hasQueryBeenCalledBefore({ getUser: { id } });
      if (hasQueryBeenCalledBefore || hasQueryBeenCalledBefore == "notFound") { 
        observer.next(hasQueryBeenCalledBefore !== "notFound" ? hasQueryBeenCalledBefore : null);

      } else {
        this.getOptions().subscribe(options => {
          let api = ENV.api + '/api/users/' + id;
          this.http.get(api, options).subscribe(
            result => {
              this.storageProvider.setCachedQueryData({ getUser: { id } }, result);
              observer.next(result);
            },
            error => {
              observer.error(error);
            }
          );
        });
      }
    });
  }

  getOptions(contentType: string = 'application/json') {
    let observer;
    let obs = Observable.create(obs => {
      observer = obs;
      if (this.userData && this.userData.jwtData) {
        this.jwtGetToken().subscribe(
          result => {
            const options = {
              headers: new HttpHeaders({
                Authorization: 'Bearer ' + result
              })
            };
            if (contentType) options.headers['Content-Type'] = contentType;
            observer.next(options);
          },
          error => {
            let message =
              'Token could not be refreshed, try to login in again.';
            let alert = this.alertCtrl.create({
              title: 'Error!',
              message: message,
              buttons: ['OK']
            });
            alert.present();
          }
        );
      } else {
        observer.error('cant get token');
      }
    });

    return obs;
  }

  jwtGetToken() {
    return Observable.create(observer => {
      let token = this.userData.jwtData.jwtResponse.access_token;
      let updateTime = isNaN(
        this.userData.jwtData.jwtResponse.updated +
          this.userData.jwtData.jwtResponse.expires_in * 1000
      )
        ? 0
        : this.userData.jwtData.jwtResponse.updated +
          this.userData.jwtData.jwtResponse.expires_in * 1000;
      let shouldUpdate = new Date().getTime() >= updateTime;
      if (shouldUpdate) {
        this.loginWithToken().subscribe(
          result => {
            observer.next(this.userData.jwtData.jwtResponse.access_token);
          },
          e => {
            observer.error(e);
          }
        );
      } else {
        observer.next(this.userData.jwtData.jwtResponse.access_token);
      }
    });
  }

  initGraphQL() {
    return Observable.create(observer => {
      const defaultOptions: DefaultOptions = {
        watchQuery: {
          fetchPolicy: 'network-only',
          errorPolicy: 'ignore'
        },
        query: {
          fetchPolicy: 'network-only',
          errorPolicy: 'all'
        }
      };
      this.jwtGetToken().subscribe(
        result => {
          const graphLink = this.httpLink.create({
            uri: ENV.base + '/graphql-server/graphql',
            headers: new HttpHeaders({
              Authorization: 'Bearer ' + result // need to fix when logged in
            })
          });
          const cache = new InMemoryCache();
          try {
            this.apollo.createDefault({
              link: graphLink,
              cache: cache,
              defaultOptions
            });
          } catch (e) {
            this.apollo.getClient().link = graphLink;
          }
          observer.next('complete');
        },
        error => {
          let message = 'Token could not be refreshed, try to login in again.';
          let alert = this.alertCtrl.create({
            title: 'Error!',
            message: message,
            buttons: ['OK']
          });
          alert.present();
          observer.error(error);
        }
      );
    });
  }

  registerFCM() {
    return Observable.create(observer => {
      this.getOptions().subscribe(options => {
        const api = ENV.base + '/geyser-adapter/me/fcm';
        const token = '';

        // Handle notifications clicks
        // this.fcm.onNotification().subscribe(data => {
        //   if(data.wasTapped){
        //   } else {
        //   };
        // });
        if (this.platform.is('cordova')) {
          this.fcm.getToken().then(token => {
            // backend.registerToken(token);
            observer.next(token);
            this.http.post(api, { token }, options).subscribe(
              result => {
                observer.next(result);
              },
              error => {
                observer.error(error);
              }
            );
          });
          this.fcm.getToken().then(token => {
            // backend.registerToken(token);
            observer.next(token);
            // this.http.post(api, token, options).subscribe(
            //   result => {
            //     observer.next(result);
            //   },
            //   error => {
            //     observer.error(error);
            //   }
            // );
          });
        } else {
          observer.next('non cordova token');
        }
      });
    });
  }

  getScheduleCompany(){
    return this.userData?.data?.companies?.find(company => !company.includes('snn:group:company:plumber-connect-'))
  }
}



// WEBPACK FOOTER //
// ./src/providers/auth/auth.ts